/**
 * useVerknuepfungenFilter — keeps a `VerknuepfungenFilter` in sync with the
 * `?links=...` query parameter in the URL (Issue #332).
 *
 * - Reads the current value from `window.location.search` on mount and when
 *   the browser fires `popstate` (back/forward button, external deep-link).
 * - When `setFilter(next)` is called, writes the new value into the URL via
 *   `history.pushState` so Browser-Back returns to the previous filter, and
 *   re-dispatches a `popstate` event so other hooks listening on the URL stay
 *   in sync.
 * - Leaves other query parameters (status, bu, ...) untouched so this hook can
 *   coexist with the FallFilterBar from #264.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  parseVerknuepfungenFilter,
  serializeVerknuepfungenFilter,
  type VerknuepfungenFilter,
} from '@/lib/verknuepfungen-filter';

const LINKS_KEY = 'links';

function readFromUrl(): VerknuepfungenFilter {
  if (typeof window === 'undefined') return {};
  return parseVerknuepfungenFilter(window.location.search);
}

function writeToUrl(next: VerknuepfungenFilter): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  params.delete(LINKS_KEY);
  const serialised = serializeVerknuepfungenFilter(next);
  if (serialised) {
    const [, value] = serialised.split('=');
    params.set(LINKS_KEY, value);
  }
  const query = params.toString();
  const href = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
  window.history.pushState({}, '', href);
  // Notify other listeners (FallFilterBar etc.) that the URL changed.
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useVerknuepfungenFilter(): {
  filter: VerknuepfungenFilter;
  setFilter: (f: VerknuepfungenFilter) => void;
} {
  const [filter, setFilterState] = useState<VerknuepfungenFilter>(readFromUrl);

  useEffect(() => {
    const onPop = () => setFilterState(readFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setFilter = useCallback((next: VerknuepfungenFilter) => {
    writeToUrl(next);
    setFilterState(next);
  }, []);

  return { filter, setFilter };
}
